import React, { useEffect, useRef, useState } from "react";
import { CSSTransition } from "react-transition-group";
import cn from "classnames";

import { SpriteIcon } from "@litres-libs/sprite-icons";

import { useAnimationIn } from "../../hooks/useAnimationIn";
import { ANIMATION_TIME } from "../../utils";
import type { BottomSheetProps } from "../../BottomSheet";

import styles from "./BottomSheetContent.module.scss";
import { useLocale } from "../../../../locale";

export const BottomSheetContent = (props: BottomSheetProps) => {
  const {
    open,
    title,
    onClose,
    children,
    wrapperClassName,
    containerClassName,
    contentTestId = "overlay__container",
  } = props;
  const { t } = useLocale();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const touchPositionStartY = useRef(0);
  const isMoved = useRef(false);

  const [offsetTop, setOffsetTop] = useState(0);

  const isAnimationIn = useAnimationIn(open);

  const handleTouchStart = (e: TouchEvent) => {
    touchPositionStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = () => {
    if (isMoved.current) {
      onClose();
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    e.preventDefault();
    const distance = e.touches[0].clientY - touchPositionStartY.current;

    if (distance >= 0) {
      setOffsetTop(offset => offset + distance);
      isMoved.current = true;
      touchPositionStartY.current = e.touches[0].clientY;
    }
  };

  useEffect(() => {
    const isTouchDevice =
      "ontouchstart" in window ||
      navigator.maxTouchPoints > 0 ||
      // TODO: review whether we need this check
      // @ts-ignore
      navigator.msMaxTouchPoints > 0;

    if (!isTouchDevice) {
      console.error(
        "Компонент BottomSheet из ui-kit был открыт не на touch-устройстве. Это неправильное использование компонента."
      );
    }

    const header = headerRef.current;

    if (header) {
      header.addEventListener("touchstart", handleTouchStart);
      header.addEventListener("touchend", handleTouchEnd);
      header.addEventListener("touchmove", handleTouchMove);
    }

    return () => {
      if (header) {
        header.removeEventListener("touchstart", handleTouchStart);
        header.removeEventListener("touchend", handleTouchEnd);
        header.removeEventListener("touchmove", handleTouchMove);
      }
    };
  }, []);

  return (
    <CSSTransition
      timeout={ANIMATION_TIME}
      nodeRef={containerRef}
      in={isAnimationIn}
      classNames={{
        enter: styles.container__enter,
        enterActive: styles.container__enter_active,
        exit: styles.container__exit,
        exitActive: styles.container__exit_active,
      }}
    >
      <div
        ref={containerRef}
        className={cn(styles.container, containerClassName)}
        style={{ transform: `translateY(${offsetTop}px)` }}
        data-testid={contentTestId}
      >
        <div
          className={styles.header}
          ref={headerRef}
          data-testid="overlay__header"
        >
          {!!title && <h4 className={styles.header__title}>{title}</h4>}
          <button
            type="button"
            aria-label={t("Закрыть")}
            tabIndex={0}
            className={styles.header__closeButton}
            onClick={onClose}
            data-testid="overlay__closeButton"
          >
            <SpriteIcon name="close" size={24} />
          </button>
        </div>
        <div className={cn(styles.content, wrapperClassName)}>{children}</div>
      </div>
    </CSSTransition>
  );
};
